import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Circle } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import SaveJobButton from './SaveJobButton';
import { savedSearchesAPI } from '../api/api';
import axiosInstance from '../api/api';
import { toast } from 'sonner';
import { 
  MapPin, DollarSign, Clock, Briefcase, ExternalLink, 
  Layers, Search, MapPinned, X, Minus, Plus,
  Map as MapIcon, Satellite, Mountain, Bookmark, Star, Trash2, ChevronDown,
  Navigation, Loader2
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for jobs
const createJobMarkerIcon = (isHighlighted = false) => {
  return L.divIcon({
    className: 'custom-job-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${isHighlighted ? 'linear-gradient(135deg, #1d4ed8, #1e40af)' : 'linear-gradient(135deg, #3b82f6, #2563eb)'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(37,99,235,0.35)${isHighlighted ? ', 0 0 0 10px rgba(37,99,235,0.18)' : ', 0 0 0 6px rgba(37,99,235,0.12)'};
        border: 3px solid white;
      ">
        <svg style="transform: rotate(45deg); width: 16px; height: 16px; fill: white;" viewBox="0 0 24 24">
          <path d="M20 6h-3V4c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM9 4h6v2H9V4zm11 15H4V8h16v11z"/>
        </svg>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
};

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let dimensions = 40;
  
  if (count >= 10 && count < 50) {
    size = 'medium';
    dimensions = 50;
  } else if (count >= 50) {
    size = 'large';
    dimensions = 60;
  }
  
  const colors = {
    small: 'from-blue-500 to-blue-600',
    medium: 'from-orange-500 to-orange-600',
    large: 'from-red-500 to-red-600'
  };
  
  return L.divIcon({
    html: `
      <div style="
        width: ${dimensions}px;
        height: ${dimensions}px;
        background: linear-gradient(135deg, ${size === 'small' ? '#3b82f6, #2563eb' : size === 'medium' ? '#2563eb, #1d4ed8' : '#1d4ed8, #1e40af'});
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${size === 'large' ? '16px' : '14px'};
        box-shadow: 0 4px 15px rgba(37,99,235,0.35), 0 0 0 6px rgba(37,99,235,0.12);
        border: 3px solid white;
      ">
        ${count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: L.point(dimensions, dimensions, true),
  });
};

// Custom marker for user's current location
const createUserLocationIcon = () => {
  return L.divIcon({
    className: 'user-location-marker',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background: linear-gradient(135deg, #22c55e, #16a34a);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 0 8px rgba(34, 197, 94, 0.3), 0 4px 12px rgba(0,0,0,0.3);
        border: 3px solid white;
        animation: pulse-ring 2s ease-out infinite;
      ">
        <div style="width: 8px; height: 8px; background: white; border-radius: 50%;"></div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Map bounds fitter component
const MapBoundsFitter = ({ jobs, radiusCenter }) => {
  const map = useMap();
  
  useEffect(() => {
    if (radiusCenter) {
      map.setView([radiusCenter.lat, radiusCenter.lng], 10);
      return;
    }
    
    if (jobs.length > 0) {
      const validJobs = jobs.filter(job => job.coordinates?.lat && job.coordinates?.lng);
      if (validJobs.length > 0) {
        const bounds = L.latLngBounds(
          validJobs.map(job => [job.coordinates.lat, job.coordinates.lng])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [jobs, map, radiusCenter]);
  
  return null;
};

// Map event handler for "Search this area"
const MapEventHandler = ({ onBoundsChange, onMapMove }) => {
  const map = useMapEvents({
    moveend: () => {
      const bounds = map.getBounds();
      const center = map.getCenter();
      onBoundsChange(bounds);
      onMapMove(center);
    },
    zoomend: () => {
      const bounds = map.getBounds();
      onBoundsChange(bounds);
    }
  });
  return null;
};

// US city coordinates mapping for geocoding fallback
const US_CITY_COORDS = {
  'los angeles': { lat: 34.0522, lng: -118.2437 },
  'new york': { lat: 40.7128, lng: -74.0060 },
  'chicago': { lat: 41.8781, lng: -87.6298 },
  'houston': { lat: 29.7604, lng: -95.3698 },
  'phoenix': { lat: 33.4484, lng: -112.0740 },
  'san diego': { lat: 32.7157, lng: -117.1611 },
  'dallas': { lat: 32.7767, lng: -96.7970 },
  'san francisco': { lat: 37.7749, lng: -122.4194 },
  'seattle': { lat: 47.6062, lng: -122.3321 },
  'denver': { lat: 39.7392, lng: -104.9903 },
  'miami': { lat: 25.7617, lng: -80.1918 },
  'atlanta': { lat: 33.7490, lng: -84.3880 },
  'boston': { lat: 42.3601, lng: -71.0589 },
  'austin': { lat: 30.2672, lng: -97.7431 },
  'portland': { lat: 45.5051, lng: -122.6750 },
  'las vegas': { lat: 36.1699, lng: -115.1398 },
  'orlando': { lat: 28.5383, lng: -81.3792 },
  'sacramento': { lat: 38.5816, lng: -121.4944 },
  'san jose': { lat: 37.3382, lng: -121.8863 },
  'philadelphia': { lat: 39.9526, lng: -75.1652 },
  'nashville': { lat: 36.1627, lng: -86.7816 },
  'charlotte': { lat: 35.2271, lng: -80.8431 },
  'detroit': { lat: 42.3314, lng: -83.0458 },
  'minneapolis': { lat: 44.9778, lng: -93.2650 },
  'tampa': { lat: 27.9506, lng: -82.4572 },
  'san antonio': { lat: 29.4241, lng: -98.4936 },
  'riverside': { lat: 33.9533, lng: -117.3962 },
  'oakland': { lat: 37.8044, lng: -122.2712 },
  'long beach': { lat: 33.7701, lng: -118.1937 },
  'kansas city': { lat: 39.0997, lng: -94.5786 },
  'fresno': { lat: 36.7378, lng: -119.7871 },
  'tucson': { lat: 32.2226, lng: -110.9747 },
  'albuquerque': { lat: 35.0844, lng: -106.6504 },
  'mesa': { lat: 33.4152, lng: -111.8315 },
  'bakersfield': { lat: 35.3733, lng: -119.0187 },
  'default': { lat: 39.8283, lng: -98.5795 } // Center of US
};

// Simple geocoding function using city lookup
const geocodeLocation = (location) => {
  if (!location) return null;
  
  const locationLower = location.toLowerCase().trim();
  
  // Check for exact city match
  for (const [city, coords] of Object.entries(US_CITY_COORDS)) {
    if (city !== 'default' && locationLower.includes(city)) {
      // Add slight randomness to prevent markers stacking
      return {
        lat: coords.lat + (Math.random() - 0.5) * 0.05,
        lng: coords.lng + (Math.random() - 0.5) * 0.05
      };
    }
  }
  
  // Check for state abbreviations and return a random location in that general area
  const stateCoords = {
    'ca': { lat: 36.7783, lng: -119.4179 },
    'tx': { lat: 31.9686, lng: -99.9018 },
    'fl': { lat: 27.6648, lng: -81.5158 },
    'ny': { lat: 42.1657, lng: -74.9481 },
    'wa': { lat: 47.7511, lng: -120.7401 },
    'az': { lat: 34.0489, lng: -111.0937 },
    'nv': { lat: 38.8026, lng: -116.4194 },
    'co': { lat: 39.5501, lng: -105.7821 },
    'ga': { lat: 32.1656, lng: -82.9001 },
    'nc': { lat: 35.7596, lng: -79.0193 },
    'or': { lat: 43.8041, lng: -120.5542 },
    'illinois': { lat: 40.6331, lng: -89.3985 },
    'ohio': { lat: 40.4173, lng: -82.9071 },
    'michigan': { lat: 44.3148, lng: -85.6024 },
    'pennsylvania': { lat: 41.2033, lng: -77.1945 },
  };
  
  for (const [state, coords] of Object.entries(stateCoords)) {
    if (locationLower.includes(state)) {
      return {
        lat: coords.lat + (Math.random() - 0.5) * 0.5,
        lng: coords.lng + (Math.random() - 0.5) * 0.5
      };
    }
  }
  
  // Return random US location if nothing matches
  return {
    lat: US_CITY_COORDS.default.lat + (Math.random() - 0.5) * 10,
    lng: US_CITY_COORDS.default.lng + (Math.random() - 0.5) * 20
  };
};

// Calculate distance between two points (Haversine formula)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Tile layer configurations
const TILE_LAYERS = {
  standard: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    name: 'Standard'
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    name: 'Satellite'
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    name: 'Terrain'
  }
};

const JobMapView = ({ jobs, loading, onSearchArea }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [mappedJobs, setMappedJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [mapStyle, setMapStyle] = useState('standard');
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  const [showRadiusSearch, setShowRadiusSearch] = useState(false);
  const [radiusCenter, setRadiusCenter] = useState(null);
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [radiusLocation, setRadiusLocation] = useState('');
  const [showSearchArea, setShowSearchArea] = useState(false);
  const [currentBounds, setCurrentBounds] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [hasMovedMap, setHasMovedMap] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [defaultLoaded, setDefaultLoaded] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [matchScores, setMatchScores] = useState({});
  const mapRef = useRef(null);

  // Fetch personalized match scores (workers only) so map lead cards show "Automatic Match Score"
  useEffect(() => {
    if (!isLoggedIn || localStorage.getItem('userType') !== 'worker') return;
    axiosInstance.get('/matching/jobs-for-worker?limit=50&min_score=0')
      .then(({ data }) => {
        if (data?.success) {
          const scores = {};
          (data.jobs || []).forEach(j => { scores[j.job_id] = { score: j.match_score, level: j.match_level }; });
          setMatchScores(scores);
        }
      })
      .catch(() => {});
  }, [isLoggedIn]);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Fetch saved searches and load default on mount (if logged in)
  useEffect(() => {
    if (isLoggedIn) {
      fetchSavedSearches();
      loadDefaultSearch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [isLoggedIn]);

  const fetchSavedSearches = async () => {
    try {
      const response = await savedSearchesAPI.getAll();
      setSavedSearches(response.data.searches || []);
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    }
  };

  const loadDefaultSearch = async () => {
    if (defaultLoaded) return; // Only load once
    
    try {
      const response = await savedSearchesAPI.getDefault();
      if (response.data.default) {
        const defaultSearch = response.data.default;
        setRadiusLocation(defaultSearch.location);
        setRadiusCenter({ lat: defaultSearch.lat, lng: defaultSearch.lng });
        setRadiusMiles(defaultSearch.radius);
        setShowRadiusSearch(true);
        setDefaultLoaded(true);
        toast.success(`Loaded default: ${defaultSearch.name}`, { duration: 2000 });
      }
    } catch (error) {
      console.error('Error loading default search:', error);
    }
  };

  const handleSaveSearch = async () => {
    if (!radiusCenter || !radiusLocation.trim()) {
      toast.error('Please search for a location first');
      return;
    }
    
    const name = searchName.trim() || radiusLocation;
    
    setSavingSearch(true);
    try {
      await savedSearchesAPI.create({
        name,
        location: radiusLocation,
        lat: radiusCenter.lat,
        lng: radiusCenter.lng,
        radius: radiusMiles
      });
      toast.success('Search saved!');
      setShowSaveModal(false);
      setSearchName('');
      fetchSavedSearches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };

  const handleLoadSavedSearch = (search) => {
    setRadiusLocation(search.location);
    setRadiusCenter({ lat: search.lat, lng: search.lng });
    setRadiusMiles(search.radius);
    setShowSavedSearches(false);
    setShowRadiusSearch(true);
    toast.success(`Loaded: ${search.name}`);
  };

  const handleDeleteSavedSearch = async (searchId, e) => {
    e.stopPropagation();
    try {
      await savedSearchesAPI.delete(searchId);
      toast.success('Search deleted');
      fetchSavedSearches();
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  const handleToggleDefault = async (search, e) => {
    e.stopPropagation();
    try {
      if (search.is_default) {
        await savedSearchesAPI.clearDefault(search.id);
        toast.success('Default cleared');
      } else {
        await savedSearchesAPI.setDefault(search.id);
        toast.success(`'${search.name}' is now your default`);
      }
      fetchSavedSearches();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  // Process jobs to add coordinates
  useEffect(() => {
    const processedJobs = jobs.map(job => {
      if (job.coordinates?.lat && job.coordinates?.lng) {
        return job;
      }
      const coords = geocodeLocation(job.location);
      return { ...job, coordinates: coords };
    }).filter(job => job.coordinates);
    
    setMappedJobs(processedJobs);
    setFilteredJobs(processedJobs);
  }, [jobs]);

  // Filter jobs by radius when radius search is active
  useEffect(() => {
    if (radiusCenter && radiusMiles) {
      const filtered = mappedJobs.filter(job => {
        const distance = calculateDistance(
          radiusCenter.lat, radiusCenter.lng,
          job.coordinates.lat, job.coordinates.lng
        );
        return distance <= radiusMiles;
      });
      setFilteredJobs(filtered);
    } else {
      setFilteredJobs(mappedJobs);
    }
  }, [radiusCenter, radiusMiles, mappedJobs]);

  const handleBoundsChange = useCallback((bounds) => {
    setCurrentBounds(bounds);
    if (hasMovedMap) {
      setShowSearchArea(true);
    }
  }, [hasMovedMap]);

  const handleMapMove = useCallback((center) => {
    setMapCenter(center);
    setHasMovedMap(true);
  }, []);

  const handleSearchThisArea = () => {
    if (currentBounds && onSearchArea) {
      const boundsData = {
        north: currentBounds.getNorth(),
        south: currentBounds.getSouth(),
        east: currentBounds.getEast(),
        west: currentBounds.getWest()
      };
      onSearchArea(boundsData);
    }
    setShowSearchArea(false);
    setHasMovedMap(false);
  };

  const handleRadiusSearch = () => {
    if (!radiusLocation.trim()) return;
    
    const coords = geocodeLocation(radiusLocation);
    if (coords) {
      setRadiusCenter(coords);
    }
  };

  const clearRadiusSearch = () => {
    setRadiusCenter(null);
    setRadiusLocation('');
    setShowRadiusSearch(false);
  };

  // Geolocation - Use My Location
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setRadiusCenter({ lat: latitude, lng: longitude });
        setRadiusLocation('My Location');
        setShowRadiusSearch(true);
        setGettingLocation(false);
        toast.success('Location found! Showing jobs near you.');
      },
      (error) => {
        setGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location access denied. Please enable location permissions in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again.');
            break;
          default:
            toast.error('Unable to get your location. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes cache
      }
    );
  };

  if (loading) {
    return (
      <div className="h-[600px] bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4">Loading map...</p>
        </div>
      </div>
    );
  }

  if (mappedJobs.length === 0) {
    return (
      <div className="h-[600px] bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center">
        <div className="text-center">
          <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No jobs to display on map</h3>
          <p className="text-gray-500 dark:text-gray-400">Try adjusting your filters to see more results</p>
        </div>
      </div>
    );
  }

  const defaultCenter = [39.8283, -98.5795];
  const currentTileLayer = TILE_LAYERS[mapStyle];

  return (
    <div className="relative" data-testid="job-map-view">
      <div className="h-[600px] rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
        <MapContainer
          center={defaultCenter}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <TileLayer
            key={mapStyle}
            attribution={currentTileLayer.attribution}
            url={currentTileLayer.url}
          />
          <MapBoundsFitter jobs={filteredJobs} radiusCenter={radiusCenter} />
          <MapEventHandler onBoundsChange={handleBoundsChange} onMapMove={handleMapMove} />
          
          {/* Radius circle overlay */}
          {radiusCenter && (
            <Circle
              center={[radiusCenter.lat, radiusCenter.lng]}
              radius={radiusMiles * 1609.34} // Convert miles to meters
              pathOptions={{
                color: '#3b82f6',
                fillColor: '#3b82f6',
                fillOpacity: 0.1,
                weight: 2,
                dashArray: '5, 10'
              }}
            />
          )}
          
          {/* User's current location marker */}
          {userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={createUserLocationIcon()}
              zIndexOffset={1000}
            >
              <Popup>
                <div className="text-center p-2">
                  <p className="font-bold text-gray-800">Your Location</p>
                  <p className="text-xs text-gray-500">
                    {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Marker clustering */}
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={createClusterCustomIcon}
            maxClusterRadius={50}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
          >
            {filteredJobs.map((job) => (
              <Marker
                key={job._id}
                position={[job.coordinates.lat, job.coordinates.lng]}
                icon={createJobMarkerIcon(selectedJob === job._id)}
                eventHandlers={{
                  click: () => setSelectedJob(job._id),
                }}
              >
                <Popup maxWidth={350} className="job-map-popup">
                  <div className="p-1" data-testid={`map-popup-${job._id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Briefcase className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1 line-clamp-2">
                          {job.title}
                        </h3>
                        <p className="text-xs text-gray-600">{job.company}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center text-xs text-gray-600">
                        <MapPin className="h-3 w-3 mr-1.5 text-gray-400" />
                        <span className="truncate">{job.location}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <DollarSign className="h-3 w-3 mr-1.5 text-green-500" />
                        <span className="font-semibold">
                          ${job.budget_min?.toLocaleString() || '0'} - ${job.budget_max?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Clock className="h-3 w-3 mr-1.5 text-gray-400" />
                        <span>{job.duration || 'Duration TBD'}</span>
                      </div>
                    </div>
                    
                    {job.required_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {job.required_skills.slice(0, 3).map((skill, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700"
                          >
                            {skill}
                          </Badge>
                        ))}
                        {job.required_skills.length > 3 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600">
                            +{job.required_skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {matchScores[job._id] && (
                      <div
                        className="flex items-center justify-between bg-emerald-100 text-emerald-700 rounded-full px-3 py-1.5 mb-3 border border-emerald-200"
                        data-testid={`map-match-score-${job._id}`}
                      >
                        <span className="text-[11px] font-semibold">Automatic Match Score</span>
                        <span className="text-sm font-bold">{Math.round(matchScores[job._id].score)}%</span>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 h-8 text-xs bg-blue-600 hover:bg-blue-700"
                        onClick={() => navigate(`/job/${job._id}`)}
                        data-testid={`map-view-job-${job._id}`}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View Details
                      </Button>
                      <SaveJobButton jobId={job._id} size="sm" />
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
      
      {/* Search This Area Button */}
      {showSearchArea && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000]">
          <Button
            onClick={handleSearchThisArea}
            className="bg-white text-gray-800 hover:bg-gray-100 shadow-lg border border-gray-200"
            data-testid="search-this-area-btn"
          >
            <Search className="h-4 w-4 mr-2" />
            Search this area
          </Button>
        </div>
      )}
      
      {/* Radius Search Panel */}
      {showRadiusSearch && (
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-200 z-[1000] w-72">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <MapPinned className="h-4 w-4 text-blue-600" />
              Nearby Jobs
            </h4>
            <button onClick={clearRadiusSearch} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Location</label>
              <input
                type="text"
                value={radiusLocation}
                onChange={(e) => setRadiusLocation(e.target.value)}
                placeholder="Enter city name..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && handleRadiusSearch()}
                data-testid="radius-location-input"
              />
            </div>
            
            <div>
              <label className="text-xs text-gray-600 mb-1 block">
                Radius: <span className="font-semibold text-blue-600">{radiusMiles} miles</span>
              </label>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setRadiusMiles(Math.max(5, radiusMiles - 5))}
                  className="p-1 rounded bg-gray-100 hover:bg-gray-200"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(Number(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  data-testid="radius-slider"
                />
                <button 
                  onClick={() => setRadiusMiles(Math.min(100, radiusMiles + 5))}
                  className="p-1 rounded bg-gray-100 hover:bg-gray-200"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <Button 
              onClick={handleRadiusSearch}
              className="w-full bg-blue-600 hover:bg-blue-700"
              size="sm"
              data-testid="radius-search-btn"
            >
              <Search className="h-4 w-4 mr-2" />
              Find Jobs Within {radiusMiles} Miles
            </Button>
            
            {radiusCenter && (
              <>
                <p className="text-xs text-center text-green-600 font-medium">
                  Found {filteredJobs.length} jobs within {radiusMiles} miles
                </p>
                
                {/* Save Search Button */}
                {isLoggedIn && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveModal(true)}
                    className="w-full border-amber-500 text-amber-600 hover:bg-amber-50"
                    data-testid="save-search-btn"
                  >
                    <Bookmark className="h-4 w-4 mr-2" />
                    Save This Search
                  </Button>
                )}
              </>
            )}
            
            {/* Saved Searches List */}
            {isLoggedIn && savedSearches.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <button
                  onClick={() => setShowSavedSearches(!showSavedSearches)}
                  className="flex items-center justify-between w-full text-xs font-medium text-gray-600 hover:text-gray-800"
                  data-testid="saved-searches-toggle"
                >
                  <span className="flex items-center gap-1">
                    <Star className="h-3 w-3 text-amber-500" />
                    Saved Searches ({savedSearches.length})
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showSavedSearches ? 'rotate-180' : ''}`} />
                </button>
                
                {showSavedSearches && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {savedSearches.map((search) => (
                      <div
                        key={search.id}
                        onClick={() => handleLoadSavedSearch(search)}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group ${
                          search.is_default 
                            ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100' 
                            : 'bg-gray-50 hover:bg-blue-50'
                        }`}
                        data-testid={`saved-search-${search.id}`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <button
                            onClick={(e) => handleToggleDefault(search, e)}
                            className={`p-0.5 rounded transition-colors ${
                              search.is_default 
                                ? 'text-amber-500' 
                                : 'text-gray-300 hover:text-amber-400'
                            }`}
                            title={search.is_default ? 'Remove as default' : 'Set as default'}
                            data-testid={`default-toggle-${search.id}`}
                          >
                            <Star className={`h-3.5 w-3.5 ${search.is_default ? 'fill-amber-500' : ''}`} />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate flex items-center gap-1">
                              {search.name}
                              {search.is_default && (
                                <span className="text-[9px] bg-amber-200 text-amber-700 px-1 rounded">DEFAULT</span>
                              )}
                            </p>
                            <p className="text-[10px] text-gray-500">{search.radius} mi radius</p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteSavedSearch(search.id, e)}
                          className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-search-${search.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Save Search Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" onClick={() => setShowSaveModal(false)}>
          <div 
            className="bg-white rounded-xl p-6 w-80 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-amber-500" />
              Save Map Search
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Search Name</label>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder={radiusLocation || "e.g., Home Area"}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="search-name-input"
                />
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-gray-600">
                  <MapPinned className="h-4 w-4 inline mr-1 text-blue-500" />
                  {radiusLocation}
                </p>
                <p className="text-gray-500 text-xs mt-1">{radiusMiles} mile radius</p>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSearch}
                  disabled={savingSearch}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="confirm-save-search-btn"
                >
                  {savingSearch ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Job count indicator */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border border-gray-200 z-[1000]">
        <p className="text-sm font-medium text-gray-700">
          <span className="text-blue-600">{filteredJobs.length}</span> 
          {radiusCenter ? ` jobs within ${radiusMiles} mi` : ' jobs on map'}
        </p>
      </div>
      
      {/* Map Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-[1000]">
        {/* Use My Location Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleUseMyLocation}
          disabled={gettingLocation}
          className={`bg-white/95 backdrop-blur-sm shadow-lg border ${
            userLocation 
              ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' 
              : 'text-gray-700 hover:bg-gray-100'
          }`}
          data-testid="use-my-location-btn"
        >
          {gettingLocation ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Locating...
            </>
          ) : (
            <>
              <Navigation className="h-4 w-4 mr-2" />
              {userLocation ? 'Located' : 'My Location'}
            </>
          )}
        </Button>
        
        {/* Radius Search Toggle */}
        <Button
          variant={showRadiusSearch ? "default" : "outline"}
          size="sm"
          onClick={() => setShowRadiusSearch(!showRadiusSearch)}
          className={`bg-white/95 backdrop-blur-sm shadow-lg border ${showRadiusSearch ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
          data-testid="radius-toggle-btn"
        >
          <MapPinned className="h-4 w-4 mr-2" />
          Nearby
        </Button>
        
        {/* Map Style Toggle */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStyleMenu(!showStyleMenu)}
            className="bg-white/95 backdrop-blur-sm shadow-lg border text-gray-700 hover:bg-gray-100"
            data-testid="map-style-toggle"
          >
            <Layers className="h-4 w-4 mr-2" />
            Style
          </Button>
          
          {showStyleMenu && (
            <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden min-w-[140px]">
              <button
                onClick={() => { setMapStyle('standard'); setShowStyleMenu(false); }}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${mapStyle === 'standard' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                data-testid="map-style-standard"
              >
                <MapIcon className="h-4 w-4" />
                Standard
              </button>
              <button
                onClick={() => { setMapStyle('satellite'); setShowStyleMenu(false); }}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${mapStyle === 'satellite' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                data-testid="map-style-satellite"
              >
                <Satellite className="h-4 w-4" />
                Satellite
              </button>
              <button
                onClick={() => { setMapStyle('terrain'); setShowStyleMenu(false); }}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${mapStyle === 'terrain' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                data-testid="map-style-terrain"
              >
                <Mountain className="h-4 w-4" />
                Terrain
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg border border-gray-200 z-[1000]">
        <p className="text-xs font-semibold text-gray-700 mb-2">Map Legend</p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-700"></div>
            <span>Job Location</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-[8px] text-white font-bold">5</div>
            <span>Clustered Jobs</span>
          </div>
          {userLocation && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              </div>
              <span>Your Location</span>
            </div>
          )}
          {radiusCenter && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-dashed"></div>
              <span>Search Radius</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobMapView;
